import { useState, useEffect } from 'react';
import useLatestCallback from '@/hooks/useLatestCallback';
import { useNavigate } from 'react-router-dom';
import { Package, Clock, CheckCircle, Truck, Store, ChevronRight, RefreshCw, Loader2, MapPin } from 'lucide-react';
import { Card, CardContent } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Skeleton } from '@/components/ui/skeleton';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuthStore  } from '@/store';

import { API } from '@/config/api';

const statusConfig = {
  pending: { label: 'Pending', class: 'status-pending', icon: Clock },
  confirmed: { label: 'Confirmed', class: 'status-confirmed', icon: CheckCircle },
  preparing: { label: 'Preparing', class: 'status-preparing', icon: Package },
  ready: { label: 'Ready', class: 'status-ready', icon: Store },
  out_for_delivery: { label: 'Out for Delivery', class: 'status-delivered', icon: Truck },
  delivered: { label: 'Delivered', class: 'status-delivered', icon: CheckCircle },
  completed: { label: 'Completed', class: 'status-delivered', icon: CheckCircle },
  cancelled: { label: 'Cancelled', class: 'status-cancelled', icon: Clock }
};

export default function OrdersPage() {
  const navigate = useNavigate();
  const { token } = useAuthStore();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('active');

  const fetchOrders = useLatestCallback(async () => {
    try {
      const response = await axios.get(`${API}/orders`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(response.data);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
    } finally {
      setLoading(false);
    }
  });

  useEffect(() => {
    fetchOrders();
  }, [fetchOrders]);

  const activeOrders = orders.filter(o => 
    ['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery'].includes(o.status)
  );
  const pastOrders = orders.filter(o => 
    ['delivered', 'completed', 'cancelled'].includes(o.status)
  );

  const [reordering, setReordering] = useState(null);

  const handleReorder = async (e, orderId) => {
    e.stopPropagation();
    setReordering(orderId);
    try {
      const response = await axios.post(`${API}/reorder/${orderId}`, {}, {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.data.warning) {
        toast.warning(response.data.warning);
      }
      toast.success(`Added ${response.data.items_added} items to cart from ${response.data.restaurant_name}`);
      navigate('/checkout');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reorder');
    } finally {
      setReordering(null);
    }
  };

  const OrderCard = ({ order }) => {
    const status = statusConfig[order.status] || statusConfig.pending;
    const StatusIcon = status.icon;

    return (
      <Card 
        className="cursor-pointer card-hover"
        onClick={() => navigate(`/orders/${order.id}`)}
        data-testid={`order-card-${order.id}`}
      >
        <CardContent className="p-4">
          <div className="flex items-start justify-between mb-3">
            <div>
              <h3 className="font-semibold">{order.restaurant_name}</h3>
              <p className="text-sm text-muted-foreground">
                Order #{order.id} • {new Date(order.created_at).toLocaleDateString()}
              </p>
            </div>
            <Badge className={`${status.class} flex items-center gap-1`}>
              <StatusIcon className="h-3 w-3" />
              {status.label}
            </Badge>
          </div>

          <div className="flex items-center gap-2 mb-3 text-sm text-muted-foreground">
            {order.order_type === 'delivery' ? (
              <Truck className="h-4 w-4" />
            ) : (
              <Store className="h-4 w-4" />
            )}
            <span className="capitalize">{order.order_type}</span>
          </div>

          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-muted-foreground">
                {order.items.length} item{order.items.length > 1 ? 's' : ''}
              </p>
              <p className="font-semibold">${order.total.toFixed(2)}</p>
            </div>
            <div className="flex items-center gap-2">
              {['pending', 'confirmed', 'preparing', 'ready', 'out_for_delivery'].includes(order.status) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => {
                    e.stopPropagation();
                    navigate(`/live-track/${order.id}`);
                  }}
                  data-testid={`track-btn-${order.id}`}
                >
                  <MapPin className="h-4 w-4 mr-1" />
                  Track
                </Button>
              )}
              {['delivered', 'completed'].includes(order.status) && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={(e) => handleReorder(e, order.id)}
                  disabled={reordering === order.id}
                  data-testid={`reorder-btn-${order.id}`}
                >
                  {reordering === order.id ? (
                    <Loader2 className="h-4 w-4 animate-spin" />
                  ) : (
                    <>
                      <RefreshCw className="h-4 w-4 mr-1" />
                      Reorder
                    </>
                  )}
                </Button>
              )}
              <ChevronRight className="h-5 w-5 text-muted-foreground" />
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  return (
    <div className="min-h-screen bg-muted/30 py-8" data-testid="orders-page">
      <div className="max-w-4xl mx-auto px-4">
        <h1 className="text-2xl font-bold mb-6">My Orders</h1>

        <Tabs value={activeTab} onValueChange={setActiveTab}>
          <TabsList className="w-full justify-start h-auto p-1 bg-muted/50 rounded-xl mb-6">
            <TabsTrigger value="active" className="rounded-lg">
              Active ({activeOrders.length})
            </TabsTrigger>
            <TabsTrigger value="past" className="rounded-lg">
              Past Orders ({pastOrders.length})
            </TabsTrigger>
          </TabsList>

          <TabsContent value="active">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <Skeleton className="h-5 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-8 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : activeOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No active orders</h3>
                <p className="text-muted-foreground">Your active orders will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {activeOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            )}
          </TabsContent>

          <TabsContent value="past">
            {loading ? (
              <div className="space-y-4">
                {[...Array(3)].map((_, i) => (
                  <Card key={i}>
                    <CardContent className="p-4">
                      <Skeleton className="h-5 w-1/3 mb-2" />
                      <Skeleton className="h-4 w-1/2 mb-4" />
                      <Skeleton className="h-8 w-full" />
                    </CardContent>
                  </Card>
                ))}
              </div>
            ) : pastOrders.length === 0 ? (
              <div className="text-center py-12">
                <Package className="h-16 w-16 mx-auto text-muted-foreground/30 mb-4" />
                <h3 className="text-lg font-semibold mb-2">No past orders</h3>
                <p className="text-muted-foreground">Your order history will appear here</p>
              </div>
            ) : (
              <div className="space-y-4">
                {pastOrders.map((order) => (
                  <OrderCard key={order.id} order={order} />
                ))}
              </div>
            )}
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
